import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, Loader2, Pencil, Plus, Receipt, Search, Trash2, X } from "lucide-react";
import { api, fmt, apiError } from "@/lib/api";

const CATEGORIES = ["Warehouse", "Packaging", "Salary", "Utilities", "Fuel", "Marketing", "Software", "Repairs", "Office", "Other"];
const METHODS = [
  { id: "bank_transfer", label: "Bank transfer" }, { id: "upi", label: "UPI" },
  { id: "cash", label: "Cash" }, { id: "card", label: "Card" }, { id: "other", label: "Other" },
];
const EMPTY = { amount: "", category: "Warehouse", expense_date: new Date().toISOString().slice(0, 10), method: "bank_transfer", reference: "", note: "" };

const input = "h-10 w-full rounded-lg border border-white/10 bg-black/40 px-3 text-sm text-white placeholder:text-neutral-600 focus:border-amber-500/60 focus:outline-none";

function ExpenseForm({ initial, onSaved, onClose, title, testid }) {
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    const amount = parseFloat(form.amount);
    if (!amount || amount <= 0) { toast.error("Enter a valid positive amount"); return; }
    setBusy(true);
    try {
      await onSaved({ ...form, amount });
      setForm(EMPTY);
    } catch {
      // parent toasts
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-2xl border border-amber-500/25 bg-amber-500/[0.04] p-5" data-testid={testid}>
      <div className="mb-3 flex items-center justify-between">
        <p className="text-sm font-bold text-white">{title}</p>
        <button onClick={onClose} aria-label="Close" data-testid={`${testid}-close`}
          className="flex h-8 w-8 items-center justify-center rounded-lg border border-white/10 text-neutral-400 hover:text-white"><X className="h-4 w-4" /></button>
      </div>
      <div className="grid gap-3 sm:grid-cols-3">
        <div>
          <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-neutral-500">Amount ₹</label>
          <input type="number" min="0.01" step="0.01" value={form.amount} onChange={(e) => set("amount", e.target.value)}
            data-testid={`${testid}-amount`} className={input} placeholder="0.00" />
        </div>
        <div>
          <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-neutral-500">Category</label>
          <select value={form.category} onChange={(e) => set("category", e.target.value)} data-testid={`${testid}-category`} className={input}>
            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-neutral-500">Date</label>
          <input type="date" value={form.expense_date} onChange={(e) => set("expense_date", e.target.value)}
            data-testid={`${testid}-date`} className={input} />
        </div>
        <div>
          <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-neutral-500">Payment method</label>
          <select value={form.method} onChange={(e) => set("method", e.target.value)} data-testid={`${testid}-method`} className={input}>
            {METHODS.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
          </select>
        </div>
        <div>
          <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-neutral-500">Reference</label>
          <input value={form.reference} onChange={(e) => set("reference", e.target.value)} placeholder="UTR / receipt no. (optional)"
            data-testid={`${testid}-reference`} className={input} />
        </div>
        <div>
          <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-neutral-500">Note</label>
          <input value={form.note} onChange={(e) => set("note", e.target.value)} placeholder="Optional"
            data-testid={`${testid}-note`} className={input} />
        </div>
      </div>
      <button onClick={submit} disabled={busy} data-testid={`${testid}-submit`}
        className="mt-4 flex h-10 items-center gap-2 rounded-full bg-amber-500 px-6 text-xs font-bold text-black transition-colors hover:bg-amber-400 disabled:opacity-40">
        {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />} {title}
      </button>
    </div>
  );
}

export default function AdminExpenses() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/expenses", { params: { q, category, start, end } })
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(apiError(e, "Couldn't load expenses")));
  }, [q, category, start, end]);
  useEffect(() => { load(); }, [load]);

  const create = async (form) => {
    try {
      await api.post("/admin/expenses", form);
      toast.success("Expense recorded");
      setCreating(false);
      load();
    } catch (e) { toast.error(apiError(e, "Couldn't save the expense")); throw e; }
  };

  const update = async (form) => {
    try {
      await api.put(`/admin/expenses/${editing.id}`, form);
      toast.success("Expense updated");
      setEditing(null);
      load();
    } catch (e) { toast.error(apiError(e, "Couldn't update the expense")); throw e; }
  };

  const remove = async (exp) => {
    if (!window.confirm(`Delete ${exp.expense_number} (${fmt(exp.amount)})? The audit trail is kept.`)) return;
    try {
      await api.delete(`/admin/expenses/${exp.id}`);
      toast.success("Expense deleted (audit trail kept)");
      load();
    } catch (e) { toast.error(apiError(e, "Couldn't delete the expense")); }
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await api.get("/admin/expenses/export", { params: { q, category, start, end }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "paqvero-expenses.csv"; a.click();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch (e) { toast.error(apiError(e, "Couldn't export the CSV")); }
    finally { setExporting(false); }
  };

  return (
    <div data-testid="admin-expenses" className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-white"><Receipt className="h-6 w-6 text-amber-400" /> Expenses</h1>
          <p className="mt-1 text-sm text-neutral-500">Business overheads — warehouse, salaries, fuel, everything else. These exact amounts feed the owner's Profit &amp; Margin.</p>
        </div>
        <button onClick={() => { setCreating((v) => !v); setEditing(null); }} data-testid="expense-create-btn"
          className="flex h-11 items-center gap-2 rounded-full bg-amber-500 px-5 text-sm font-bold text-black transition-colors hover:bg-amber-400">
          <Plus className="h-4 w-4" /> New expense
        </button>
      </div>

      {creating && <ExpenseForm initial={EMPTY} onSaved={create} onClose={() => setCreating(false)} title="Record expense" testid="expense-form" />}
      {editing && (
        <ExpenseForm
          initial={{ amount: String(editing.amount), category: editing.category, expense_date: editing.expense_date,
                     method: editing.method, reference: editing.reference || "", note: editing.note || "" }}
          onSaved={update} onClose={() => setEditing(null)} title={`Edit ${editing.expense_number}`} testid="expense-edit-form" />
      )}

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-600" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search ref, note, number…" data-testid="expense-search"
            className="h-10 w-64 rounded-xl border border-white/10 bg-black/40 pl-10 pr-3 text-xs text-white placeholder:text-neutral-600 focus:border-amber-500/60 focus:outline-none" />
        </div>
        <select value={category} onChange={(e) => setCategory(e.target.value)} data-testid="expense-category-filter"
          className="h-10 rounded-xl border border-white/10 bg-black/40 px-3 text-xs text-white focus:border-amber-500/60 focus:outline-none">
          <option value="">Category: all</option>
          {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="expense-start"
          className="h-10 rounded-xl border border-white/10 bg-black/40 px-3 text-xs text-white focus:border-amber-500/60 focus:outline-none" />
        <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="expense-end"
          className="h-10 rounded-xl border border-white/10 bg-black/40 px-3 text-xs text-white focus:border-amber-500/60 focus:outline-none" />
        <button onClick={exportCsv} disabled={exporting || !data} data-testid="expense-export"
          className="ml-auto inline-flex items-center gap-2 rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-2.5 text-xs font-bold text-emerald-300 transition-colors hover:bg-emerald-500/20 disabled:opacity-40">
          {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
          Export CSV
        </button>
      </div>

      {data && data.count > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="expense-breakdown">
          <span className="rounded-full border border-amber-500/30 bg-amber-500/10 px-3 py-1 text-[11px] font-bold text-amber-300">
            Total: {fmt(data.total)}
          </span>
          {Object.entries(data.by_category).map(([c, v]) => (
            <span key={c} className="rounded-full border border-white/10 bg-white/[0.04] px-3 py-1 text-[11px] font-semibold text-neutral-300">
              {c}: {fmt(v)}
            </span>
          ))}
        </div>
      )}

      {!data ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-neutral-600" /></div>
      ) : data.expenses.length === 0 ? (
        <div data-testid="expenses-empty" className="rounded-2xl border border-white/[0.07] bg-white/[0.02] py-16 text-center text-sm text-neutral-500">
          No expenses match — record one or widen the filters.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-white/[0.07]">
          <table className="w-full min-w-[860px] text-left text-sm" data-testid="expenses-table">
            <thead>
              <tr className="border-b border-white/[0.07] bg-white/[0.03] text-[11px] uppercase tracking-wider text-neutral-500">
                <th className="px-4 py-3">Expense #</th><th className="px-4 py-3">Date</th><th className="px-4 py-3">Category</th>
                <th className="px-4 py-3">Amount</th><th className="px-4 py-3">Method</th><th className="px-4 py-3">Reference</th>
                <th className="px-4 py-3">Note</th><th className="px-4 py-3">By</th><th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.expenses.map((e) => (
                <tr key={e.id} data-testid={`expense-row-${e.id}`} className="border-b border-white/[0.05] hover:bg-white/[0.02]">
                  <td className="px-4 py-3 font-mono text-xs font-bold text-amber-400">{e.expense_number}</td>
                  <td className="px-4 py-3 text-xs text-neutral-500">{e.expense_date}</td>
                  <td className="px-4 py-3">
                    <span className="rounded-full border border-white/10 bg-white/[0.05] px-2.5 py-1 text-[10px] font-bold text-neutral-300">{e.category}</span>
                  </td>
                  <td className="px-4 py-3 font-bold text-white">{fmt(e.amount)}</td>
                  <td className="px-4 py-3 text-xs text-neutral-400">{e.method.replace(/_/g, " ")}</td>
                  <td className="px-4 py-3 text-xs text-neutral-400">{e.reference || "—"}</td>
                  <td className="max-w-[180px] truncate px-4 py-3 text-xs text-neutral-500">{e.note || "—"}</td>
                  <td className="px-4 py-3 text-[11px] text-neutral-600">{e.created_by}</td>
                  <td className="px-4 py-3 text-right">
                    <span className="flex items-center justify-end gap-2">
                      <button onClick={() => { setEditing(e); setCreating(false); }} aria-label="Edit" data-testid={`expense-edit-${e.id}`}
                        className="flex h-8 w-8 items-center justify-center rounded-lg border border-white/10 text-neutral-400 hover:text-amber-300">
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      <button onClick={() => remove(e)} aria-label="Delete" data-testid={`expense-delete-${e.id}`}
                        className="flex h-8 w-8 items-center justify-center rounded-lg border border-white/10 text-neutral-400 hover:text-red-300">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
